import * as d3 from 'd3';
import { interpolateCool } from 'd3-scale-chromatic';
import { toggleClassByWidth } from '../../js/utils/d3-helpers.ts';
import { BaseChart, UIState } from '../../js/classes/BaseChart.ts';

type D3Selection<T extends d3.BaseType> = d3.Selection<T, unknown, null, undefined>;

interface PointData {
	dayOfYear: number;
	value: number;
}

interface LineSeries {
	key: string;
	color: string;
	points: PointData[];
}

interface SleepCycleUIState extends UIState {
	currentYear: string | null;
}

export class SleepCycleChart extends BaseChart<SleepCycleChartData, LineSeries, SleepCycleUIState> {
	static override defaultConfig = {
		lineWidth: 2.0,
		gridLineWidth: 1,
		// ホバー時の透過度設定
		highlightOpacity: 1.0,
		dimmedOpacity: 0.1, // 非選択時はかなり薄くする
		yAxisMin: 300,
		yAxisMax: 480,
		yAxisTickInterval: 60,
		responsiveBreakpoint: 480,
		animationDurationPerSeries: 1000,
	};

	private lineData: LineSeries[] = [];

	private xScale: d3.ScaleLinear<number, number> | null = null;
	private yScale: d3.ScaleLinear<number, number> | null = null;
	private colorScale: d3.ScaleSequential<string> | null = null;
	private lineGenerator: d3.Line<PointData> | null = null;

	private yTickValues: number[] = [];
	private seriesOrder: string[] = [];

	private linesGroup: D3Selection<SVGGElement> | null = null;
	private endpointLabelsGroup: D3Selection<SVGGElement> | null = null;
	private highlightedKey: string | null = null;
	private xAxisGroup: D3Selection<SVGGElement> | null = null;
	private yAxisGroup: D3Selection<SVGGElement> | null = null;

	private cachedPaths: D3Selection<SVGPathElement> | null = null;
	private lineLengths: Map<string, number> = new Map();

	constructor(chartElement: HTMLElement, config = {}) {
		super(chartElement, config);

		// ★追加: HTML属性から再生時間を読み取って設定を上書き
		const durationAttr = chartElement.dataset.duration;
		if (durationAttr) {
			const parsed = parseInt(durationAttr, 10);
			if (!isNaN(parsed) && parsed > 0) {
				this.config.animationDurationPerSeries = parsed;
			}
		}

		this.margin = { top: 20, right: 80, bottom: 50, left: 60 };
	}

	protected override _processData(): void {
		if (!this.fullDataSet?.meta?.seriesOrder || !this.fullDataSet.series) {
			console.error('Invalid data structure for SleepCycleChart.');
			this.seriesOrder = []; this.lineData = []; return;
		}

		this.seriesOrder = [...this.fullDataSet.meta.seriesOrder].sort();

		const yearExtent = d3.extent(this.seriesOrder.map(y => parseInt(y, 10)));
		if (!yearExtent[0] || !yearExtent[1]) return;

		const colorScale = d3.scaleSequential(interpolateCool).domain(yearExtent);
		this.colorScale = colorScale;

		this.lineData = this.seriesOrder.map((key): LineSeries | null => {
			const series = this.fullDataSet!.series[key];
			if (!series?.values) return null;

			const points: PointData[] = series.values
				.map((value, index): PointData | null =>
					(value !== null && !isNaN(value)) ? { dayOfYear: index + 1, value } : null
				)
				.filter((d): d is PointData => d !== null);

			if (points.length === 0) return null;

			return { key, color: colorScale(parseInt(key, 10)), points };
		}).filter((d): d is LineSeries => d !== null);

		this.activeLineData = this.lineData;
	}

	protected override setupLayers(): void {
		d3.select(this.chartElement).select('canvas').remove();
		d3.select(this.chartElement).select('svg').remove();

		const containerRect = this.chartElement.getBoundingClientRect();
		const totalWidth = containerRect.width;
		const totalHeight = containerRect.height;

		this.svg = d3.select(this.chartElement).append('svg')
			.attr('width', totalWidth).attr('height', totalHeight)
			.attr('viewBox', `0 0 ${totalWidth} ${totalHeight}`)
			.on('mouseleave', () => {
				if (!this.isPlaying) {
					this.highlightedKey = null;
					this._applyHighlightStyles();
				}
			});

		this.g = this.svg.append('g').attr('transform', `translate(${this.margin.left},${this.margin.top})`);

		this.linesGroup = this.g.append('g').attr('class', 'lines-group');
		this.xAxisGroup = this.g.append('g').attr('class', 'x-axis axis');
		this.yAxisGroup = this.g.append('g').attr('class', 'y-axis axis');
		this.endpointLabelsGroup = this.g.append('g').attr('class', 'endpoint-labels-group');
	}

	protected override _initialDrawAxes(): void {
		if (!this.g || !this.linesGroup) return;

		this.xScale = d3.scaleLinear().domain([1, 366]).range([0, this.width]);
		this.yScale = d3.scaleLinear().domain([this.config.yAxisMin, this.config.yAxisMax]).range([this.height, 0]);
		this.lineGenerator = d3.line<PointData>().x(d => this.xScale!(d.dayOfYear)).y(d => this.yScale!(d.value));

		const yearForAxis = this.seriesOrder.length > 0 ? parseInt(this.seriesOrder[0], 10) : new Date().getFullYear();
		const isLeap = (year: number) => (year % 4 === 0 && year % 100 !== 0) || (year % 400 === 0);
		const daysInMonths = isLeap(yearForAxis) ? [31, 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31] : [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
		const monthStarts = [1];
		d3.cumsum(daysInMonths).slice(0, 11).forEach(d => monthStarts.push(d + 1));
		const tickValues = monthStarts.map((startDay, i) => startDay + (daysInMonths[i] / 2));
		const monthLabels = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

		const xAxis = d3.axisBottom<number>(this.xScale).tickValues(tickValues).tickFormat((d, i) => monthLabels[i]).tickSizeOuter(0);
		this.xAxisGroup!.attr('transform', `translate(0, ${this.height})`).call(xAxis);

		this.yTickValues = d3.range(this.config.yAxisMin, this.config.yAxisMax + 1, this.config.yAxisTickInterval);
		const yAxis = d3.axisLeft(this.yScale).tickValues(this.yTickValues).tickFormat(d => `${d as number / 60}h`).tickPadding(8);
		this.yAxisGroup!.call(yAxis);

		this.g.selectAll('.grid-line').remove();
		this.yTickValues.forEach(tick => {
			this.g!.insert('line', ':first-child')
				.attr('class', 'grid-line')
				.attr('x1', 0).attr('x2', this.width)
				.attr('y1', this.yScale!(tick)).attr('y2', this.yScale!(tick))
				.attr('stroke', '#e9e9e9')
				.attr('stroke-width', this.config.gridLineWidth);
		});

		this.g.selectAll('.y-axis-label').remove();
		this.g.append('text').attr('class', 'axis-label y-axis-label').attr('transform', 'rotate(-90)').attr('y', 0 - this.margin.left).attr('x', 0 - this.height / 2).attr('dy', '1em').text('睡眠時間');

		this.linesGroup.selectAll('*').remove();
		this.lineLengths.clear();

		this.activeLineData.forEach(series => {
			const pathData = this.lineGenerator!(series.points);
			if (!pathData) return;

			const pathElement = this.linesGroup!.append('path')
				.attr('class', 'line-path')
				.attr('data-key', series.key)
				.attr('d', pathData)
				.style('fill', 'none')
				.style('stroke', series.color)
				.style('stroke-width', this.config.lineWidth)
				.style('stroke-linecap', 'round')
				.style('stroke-linejoin', 'round')
				.style('opacity', 0)

			const rawLength = pathElement.node()?.getTotalLength() || 0;
			const length = Math.ceil(rawLength + 100);

			this.lineLengths.set(series.key, length);

			pathElement
				.style('stroke-dasharray', length)
				.style('stroke-dashoffset', length);
		});

		this.cachedPaths = this.linesGroup.selectAll<SVGPathElement, unknown>('.line-path') as unknown as D3Selection<SVGPathElement>;

		this._setupEndpointLabels();

		this.checkAndApplyLayout();
	}

	private _setupEndpointLabels(): void {
		if (!this.endpointLabelsGroup || !this.xScale || !this.yScale) return;
		this.endpointLabelsGroup.selectAll('*').remove();

		this.activeLineData.forEach(series => {
			const lastPoint = series.points[series.points.length - 1];
			if (lastPoint) {
				this.endpointLabelsGroup!.append('text')
					.attr('class', 'endpoint-label')
					.attr('x', this.xScale!(lastPoint.dayOfYear) + 8)
					.attr('y', this.yScale!(lastPoint.value))
					.attr('dy', '0.35em')
					.style('fill', series.color)
					.style('opacity', 0) // 初期状態は非表示
					.text(series.key)
					.datum(series)
					.on('mouseover', (event: MouseEvent) => {
						if (this.isPlaying) return;
						this.highlightedKey = series.key;
						this._applyHighlightStyles();
					});
			}
		});
	}

	public override play(): void {
		if (this.isPlaying || !this.isReady || !this.g) return;

		if (this.currentFrame >= this.totalFrames - 1) {
			this.currentFrame = 0;
		}

		this.isPlaying = true;
		this.updateUI();

		const remainingRatio = 1 - (this.currentFrame / (this.totalFrames - 1 || 1));
		const totalDuration = this.config.animationDurationPerSeries * this.activeLineData.length;
		const duration = totalDuration * remainingRatio;

		const interpolator = d3.interpolate(this.currentFrame, this.totalFrames - 1);

		this.g.transition()
			.duration(duration)
			.ease(d3.easeLinear)
			.tween("frameTween", () => {
				return (t: number) => {
					this.currentFrame = interpolator(t);
					this.drawFrame(this.currentFrame);
					this.updateUI();
				};
			})
			.on("end", () => {
				this.isPlaying = false;
				this.currentFrame = this.totalFrames - 1;
				this.drawFrame(this.currentFrame);
				this.updateUI();
			});
	}

	public override pause(): void {
		if (!this.g) return;
		this.isPlaying = false;
		this.g.interrupt();
		this.updateUI();
	}

	public override seek(frame: number): void {
		if (!this.isReady) return;
		this.pause();
		this.currentFrame = Math.max(0, Math.min(frame, this.totalFrames - 1));
		this.drawFrame(this.currentFrame);
		this.updateUI();
	}

	protected override drawFrame(frame: number): void {
		if (!this.cachedPaths) return;

		const framesPerSeries = this.totalFrames / this.activeLineData.length;
		const lineLengths = this.lineLengths;

		// 1. パスの描画更新
		this.cachedPaths
			.style('stroke-dashoffset', function (d, i) {
				const key = this.getAttribute('data-key');
				if (!key) return 0;
				const totalLength = lineLengths.get(key) || 0;

				const startFrame = i * framesPerSeries;
				const endFrame = (i + 1) * framesPerSeries;

				if (frame <= startFrame) return totalLength;
				if (frame >= endFrame) return 0;

				const progress = (frame - startFrame) / framesPerSeries;
				return totalLength * (1 - progress);
			})
			.style('opacity', function (d, i) {
				const startFrame = i * framesPerSeries;
				// まだ始まっていない年は完全に隠す
				return (frame <= startFrame) ? 0 : 1;
			});

		// 2. ラベルの表示更新
		if (this.endpointLabelsGroup) {
			this.endpointLabelsGroup.selectAll('.endpoint-label')
				.style('opacity', (d: any, i) => {
					const endFrame = (i + 1) * framesPerSeries;
					return (frame >= endFrame - 1) ? 1 : 0;
				});
		}

		// 3. ハイライト状態の再適用（スタイル上書き防止）
		this._applyHighlightStyles();
	}

	private _applyHighlightStyles(): void {
		if (!this.cachedPaths) return;
		const isHighlighting = this.highlightedKey !== null;
		const highlightedKey = this.highlightedKey;
		const config = this.config;

		// 線のスタイル更新
		this.cachedPaths
			// 選択中を最前面へ移動
			.filter(function () {
				return this.getAttribute('data-key') === highlightedKey;
			})
			.raise();

		this.cachedPaths
			.style('opacity', function () {
				// アニメーション制御によるopacityが0（未開始）なら0のまま
				// 注意: d3.select(this).style('opacity') は文字列を返すためパースが必要
				// ただし、計算済みスタイルを取得すると重くなるため、
				// ここでは「drawFrameで設定されるべき論理的な状態」と
				// 「ハイライトによる修飾」を組み合わせるのが理想だが、
				// 簡易的に現状のスタイル値を参照する。
				const currentOpacity = parseFloat(d3.select(this).style('opacity'));
				if (currentOpacity === 0) return 0;

				if (!isHighlighting) return 1; // 誰も選択していなければ全員1

				const key = this.getAttribute('data-key');
				// 選択中なら1、それ以外は薄くする
				return key === highlightedKey ? 1 : config.dimmedOpacity;
			});

		// ラベルのスタイル更新
		if (this.endpointLabelsGroup) {
			this.endpointLabelsGroup.selectAll('.endpoint-label')
				.style('opacity', function () {
					const currentOpacity = parseFloat(d3.select(this).style('opacity'));
					if (currentOpacity === 0) return 0;

					if (!isHighlighting) return 1;
					const dKey = (d3.select(this).datum() as LineSeries).key;
					return dKey === highlightedKey ? 1 : config.dimmedOpacity;
				});
		}
	}

	protected override updateUI(): void {
		if (typeof this.onUpdateUICallback === 'function') {
			const state: SleepCycleUIState = {
				isPlaying: this.isPlaying,
				currentFrame: this.currentFrame,
				totalFrames: this.totalFrames,
				currentYear: this._getCurrentYearFromFrame(this.currentFrame)
			};
			this.onUpdateUICallback(state);
		}
	}

	private _getCurrentYearFromFrame(frame: number): string | null {
		if (this.activeLineData.length === 0 || this.totalFrames === 0) return null;
		const framesPerSeries = this.totalFrames / this.activeLineData.length;
		const seriesIndex = Math.min(Math.floor(frame / framesPerSeries), this.activeLineData.length - 1);
		return this.activeLineData[seriesIndex]?.key ?? null;
	}

	public checkAndApplyLayout(): void {
		if (!this.chartElement) return;
		const containerWidth = this.chartElement.clientWidth;
		const isNarrow = containerWidth < this.config.responsiveBreakpoint;

		// クラスの付け替え
		toggleClassByWidth(this.xAxisGroup, containerWidth, this.config.responsiveBreakpoint, 'is-narrow');
		toggleClassByWidth(this.yAxisGroup, containerWidth, this.config.responsiveBreakpoint, 'is-narrow');

		// ★追加: SVG属性としての dx, dy の制御
		if (this.xAxisGroup) {
			this.xAxisGroup.selectAll('.tick text')
				.attr('dx', isNarrow ? '-0.5em' : null) // 通常時はnull(デフォルトに戻す)
				.attr('dy', isNarrow ? '0.5em' : '0.71em'); // 0.71emはd3.axisBottomの標準的な高さ
		}
	}
}